import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MockInspection, MockNCR, MockCAPA } from '@/mocks/qms';
import { qcService } from '@/services/qcService';
import { toInputDate } from '@/utils/format';

interface Props {
  /** Changes whenever the live dashboard refreshes */
  refreshKey?: number;
  warehouseId?: string;
  /** Real output rows from the shop floor (already filtered by the live page) */
  productionEntries?: any[];
  workOrders?: any[];
}

function formatQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function shortDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

const severityClass = (severity: string) =>
  severity === 'CRITICAL'
    ? 'bg-rose-50 text-rose-700 border-rose-200'
    : severity === 'MAJOR'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-slate-50 text-slate-600 border-slate-200';

function Panel({ title, count, action, children }: { title: string; count: number; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl flex flex-col min-h-0 max-h-[calc(100vh-360px)] min-h-[220px]">
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100 shrink-0">
        <h2 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide">
          {title} <span className="text-[#94a3b8] font-semibold">({count})</span>
        </h2>
        {action}
      </div>
      <div className="overflow-auto min-h-0 flex-1">{children}</div>
    </div>
  );
}

const Empty = ({ text }: { text: string }) => <div className="px-4 py-6 text-center text-xs text-[#94a3b8]">{text}</div>;

export default function QualityDashboard({ refreshKey = 0, warehouseId, productionEntries = [], workOrders = [] }: Props) {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState<MockInspection[]>([]);
  const [ncrs, setNcrs] = useState<MockNCR[]>([]);
  const [capas, setCapas] = useState<MockCAPA[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([qcService.getInspections(), qcService.getNCRs(), qcService.getCAPAs()])
      .then(([inspData, ncrData, capaData]) => {
        if (!active) return;
        setInspections(inspData || []);
        setNcrs(ncrData || []);
        setCapas(capaData || []);
      })
      .catch((err) => {
        console.error('Failed to load quality data:', err);
        if (active) {
          setInspections([]);
          setNcrs([]);
          setCapas([]);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  // Inspections of the selected warehouse (records without a warehouse are shown everywhere)
  const scopedInspections = useMemo(
    () => inspections.filter((i) => !warehouseId || !i.warehouseId || i.warehouseId === warehouseId),
    [inspections, warehouseId],
  );

  const stats = useMemo(() => {
    const completed = scopedInspections.filter((i) => i.status === 'PASSED' || i.status === 'FAILED');
    const passed = completed.filter((i) => i.status === 'PASSED');
    const failedByType = (type: string) => completed.filter((i) => i.type === type && i.status === 'FAILED').length;

    const verified = capas.filter((c) => c.status === 'VERIFIED' && c.verifiedDate);
    const avgClosureDays =
      verified.length > 0
        ? verified.reduce(
            (sum, c) => sum + (new Date(c.verifiedDate as string).getTime() - new Date(c.createdAt).getTime()) / 86400000,
            0,
          ) / verified.length
        : null;

    const today = toInputDate(new Date());
    const todayEntries = productionEntries.filter((e) => e.date === today);
    const good = todayEntries.reduce((s, e) => s + e.producedQty, 0);
    const rejected = todayEntries.reduce((s, e) => s + e.rejectedQty, 0);

    return {
      completed: completed.length,
      passed: passed.length,
      passRate: completed.length > 0 ? (passed.length / completed.length) * 100 : null,
      incomingFailed: failedByType('INCOMING'),
      inprocessFailed: failedByType('IN_PROCESS'),
      finalFailed: failedByType('FINAL'),
      openNcrs: ncrs.filter((n) => n.status !== 'CLOSED').length,
      criticalNcrs: ncrs.filter((n) => n.status !== 'CLOSED' && n.severity === 'CRITICAL').length,
      avgClosureDays,
      openCapas: capas.filter((c) => c.status !== 'VERIFIED' && (c.status as string) !== 'CLOSED').length,
      rejectedToday: rejected,
      rejectionRate: good + rejected > 0 ? (rejected / (good + rejected)) * 100 : null,
    };
  }, [scopedInspections, ncrs, capas, productionEntries]);

  const pendingInspections = useMemo(
    () =>
      scopedInspections
        .filter((i) => i.status === 'PENDING' || i.status === 'IN_PROGRESS')
        .sort((a, b) => String(a.scheduledDate).localeCompare(String(b.scheduledDate))),
    [scopedInspections],
  );

  const openNcrs = useMemo(() => {
    const rank: Record<string, number> = { CRITICAL: 0, MAJOR: 1, MINOR: 2 };
    return ncrs
      .filter((n) => n.status !== 'CLOSED')
      .sort((a, b) => (rank[a.severity] ?? 3) - (rank[b.severity] ?? 3) || String(b.createdAt).localeCompare(String(a.createdAt)));
  }, [ncrs]);

  // Stages blocked on the shop floor by quality
  const blockedStages = useMemo(
    () => workOrders.filter((wo) => ['QC_PENDING', 'COMPLETED_QC_PENDING', 'QC_FAILED'].includes(wo.status)),
    [workOrders],
  );

  const passTone =
    stats.passRate === null ? 'text-[#94a3b8]' : stats.passRate >= 95 ? 'text-emerald-600' : stats.passRate >= 85 ? 'text-amber-600' : 'text-rose-600';

  const kpis = [
    {
      label: 'Inspection Pass Rate',
      value: stats.passRate === null ? '—' : `${stats.passRate.toFixed(1)}%`,
      sub: `${stats.passed} of ${stats.completed} inspections passed`,
      tone: passTone,
    },
    {
      label: 'Failed Inspections',
      value: String(stats.incomingFailed + stats.inprocessFailed + stats.finalFailed),
      sub: `Incoming ${stats.incomingFailed} · In-process ${stats.inprocessFailed} · Final ${stats.finalFailed}`,
      tone: stats.incomingFailed + stats.inprocessFailed + stats.finalFailed > 0 ? 'text-rose-600' : 'text-emerald-600',
    },
    {
      label: 'Open NCRs',
      value: String(stats.openNcrs),
      sub: stats.criticalNcrs > 0 ? `${stats.criticalNcrs} critical` : 'none critical',
      tone: stats.openNcrs > 0 ? 'text-rose-600' : 'text-emerald-600',
    },
    {
      label: 'CAPA',
      value: stats.avgClosureDays === null ? '—' : `${stats.avgClosureDays.toFixed(1)} d`,
      sub: `avg. closure · ${stats.openCapas} open`,
      tone: 'text-sky-600',
    },
    {
      label: 'Shop Floor Rejection Today',
      value: stats.rejectionRate === null ? '—' : `${stats.rejectionRate.toFixed(1)}%`,
      sub: `${formatQty(stats.rejectedToday)} unit(s) rejected`,
      tone: stats.rejectedToday > 0 ? 'text-rose-600' : 'text-emerald-600',
    },
    {
      label: 'Stages Blocked by QC',
      value: String(blockedStages.length),
      sub: `${blockedStages.filter((w) => w.status === 'QC_FAILED').length} failed · ${blockedStages.filter((w) => w.status !== 'QC_FAILED').length} awaiting inspection`,
      tone: blockedStages.length > 0 ? 'text-purple-600' : 'text-emerald-600',
    },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2.5">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border border-slate-200 rounded-xl px-3 py-2.5">
            <div className="text-[11px] text-[#64748b] font-medium truncate">{k.label}</div>
            <div className={`text-lg font-bold leading-tight ${k.tone}`}>{loading && inspections.length === 0 ? '…' : k.value}</div>
            <div className="text-[10px] text-[#94a3b8] truncate" title={k.sub}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Panel
          title="Inspections Waiting"
          count={pendingInspections.length}
          action={
            <button onClick={() => navigate('/manufacturing/qc/inprocess')} className="text-[11px] font-semibold text-indigo-600 hover:underline cursor-pointer">
              Open QC
            </button>
          }
        >
          {pendingInspections.length === 0 ? (
            <Empty text="No inspections are waiting" />
          ) : (
            <div className="divide-y divide-slate-100">
              {pendingInspections.map((i) => (
                <div key={i.id} className="px-3.5 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-[#1e293b] truncate">{i.inspectionNumber}</span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                      {String(i.type).replace('_', '-')}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#64748b] truncate">
                    {i.sourceNumber} · {i.itemName}
                  </div>
                  <div className="text-[10px] text-[#94a3b8]">
                    Scheduled {shortDate(i.scheduledDate)}
                    {i.checklistName ? ` · ${i.checklistName}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Open NCRs"
          count={openNcrs.length}
          action={
            <button onClick={() => navigate('/manufacturing/qc/ncr')} className="text-[11px] font-semibold text-indigo-600 hover:underline cursor-pointer">
              Open NCRs
            </button>
          }
        >
          {openNcrs.length === 0 ? (
            <Empty text="No open non-conformance reports" />
          ) : (
            <div className="divide-y divide-slate-100">
              {openNcrs.map((n) => (
                <div key={n.id} className="px-3.5 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-[#1e293b] truncate">{n.ncrNumber}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${severityClass(n.severity)}`}>
                      {n.severity}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#64748b] truncate">
                    {n.sourceNumber} · {n.itemName}
                  </div>
                  <div className="text-[10px] text-[#94a3b8] truncate">
                    {String(n.status).replace(/_/g, ' ')} · {formatQty(Number(n.defectQty) || 0)} {n.unit || ''} · raised {shortDate(n.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Stages Blocked by QC" count={blockedStages.length}>
          {blockedStages.length === 0 ? (
            <Empty text="No work order is waiting on quality" />
          ) : (
            <div className="divide-y divide-slate-100">
              {blockedStages.map((wo) => (
                <button
                  key={wo.id}
                  onClick={() => navigate(`/manufacturing/production-orders/${wo.productionOrderId}`)}
                  className="w-full text-left px-3.5 py-2 hover:bg-slate-50/70 cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-[#1e293b] truncate">
                      {wo.woNumber} · {wo.stageName}
                    </span>
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${
                        wo.status === 'QC_FAILED' ? 'bg-rose-50 text-rose-700' : 'bg-purple-50 text-purple-700'
                      }`}
                    >
                      {wo.status === 'QC_FAILED' ? 'QC FAILED' : 'QC PENDING'}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#64748b] truncate">
                    {wo.productionOrderNumber}
                    {wo.itemName ? ` · ${wo.itemName}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
